import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import api, { getFileUrl } from '../utils/api'
import '../styles/profilePage.css'

interface ProfilePageProps {
  onLogout: () => void
}

interface Profile {
  id?: string
  userId?: string
  prenom?: string
  nom?: string
  telephone?: string
  adresse?: string
  ville?: string
  codePostal?: string
  photo_url?: string
  photoUrl?: string
  statut?: string
  dateCreation?: string
}

export default function ProfilePage({ onLogout }: ProfilePageProps) {
  const navigate = useNavigate()
  const [profile, setProfile] = useState<Profile | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [editing, setEditing] = useState(false)
  const [formData, setFormData] = useState<Profile>({})
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [photoFile, setPhotoFile] = useState<File | null>(null)
  const [photoPreview, setPhotoPreview] = useState<string | null>(null)

  const userEmail = localStorage.getItem('userEmail')

  useEffect(() => {
    // Vérifier l'authentification
    const token = localStorage.getItem('token')
    if (!token) {
      console.log('❌ No token found, redirecting to login')
      navigate('/')
      return
    }

    if (!userEmail) {
      console.log('❌ No userEmail found in localStorage')
      setError('Utilisateur non identifié')
      setLoading(false)
      return
    }

    // Charger le profil
    loadProfile()
  }, [])

  const loadProfile = async () => {
    if (!userEmail) {
      console.error('❌ userEmail is null')
      setError('Email utilisateur manquant')
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      console.log('📝 Loading profile for email:', userEmail)
      const response = await api.get('/api/profile', {
        params: { email: userEmail }
      })
      console.log('✅ Profile loaded:', response.data)
      setProfile(response.data)
      setFormData(response.data)
      setError('')
    } catch (err: any) {
      console.error('❌ Error loading profile:', err)
      const errorMsg = err.response?.data?.error || err.message || 'Erreur lors du chargement du profil'
      setError(errorMsg)
      setProfile(null)
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setPhotoFile(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleSaveProfile = async () => {
    try {
      setError('')
      setSuccess('')
      setSaving(true)
      
      console.log('Saving profile with data:', formData);
      
      const formDataObj = new FormData()
      formDataObj.append('email', userEmail!)
      formDataObj.append('prenom', formData.prenom || '')
      formDataObj.append('nom', formData.nom || '')
      formDataObj.append('telephone', formData.telephone || '')
      formDataObj.append('adresse', formData.adresse || '')
      formDataObj.append('ville', formData.ville || '')
      formDataObj.append('codePostal', formData.codePostal || '')
      
      if (photoFile) {
        formDataObj.append('photo', photoFile)
      }

      console.log('Sending request to /api/profile');
      
      const response = await api.post('/api/profile', formDataObj)

      console.log('Response received:', response.data);

      const profileData = response.data.profile
      setProfile(profileData)
      setFormData(profileData)
      setPhotoFile(null)
      setPhotoPreview(null)
      setEditing(false)
      setSuccess('Profil mis à jour avec succès!')
      
      // Redirection vers la page d'accueil
      setTimeout(() => {
        console.log('Redirecting to /home');
        navigate('/home')
      }, 1500)
    } catch (err: any) {
      console.error('Error saving profile:', err);
      const errorMsg = err.response?.data?.error || 'Erreur lors de la mise à jour'
      setError(errorMsg)
    } finally {
      setSaving(false)
    }
  }

  const handleLogoutClick = async () => {
    try {
      const token = localStorage.getItem('token')
      await api.post('/api/logout', { token })
    } catch (err) {
      console.error('Logout error:', err)
    } finally {
      onLogout()
      navigate('/')
    }
  }

  if (loading) {
    return (
      <div className="profile-container">
        <div className="profile-card">
          <div className="loading">Chargement du profil...</div>
        </div>
      </div>
    )
  }

  return (
    <div className="profile-container">
      <div className="profile-card">
        <div className="profile-header">
          <button className="btn-back" onClick={() => navigate('/home')}>
            ← Retour
          </button>
          <h1>Mon Profil</h1>
          <button className="btn-logout" onClick={handleLogoutClick}>
            Déconnexion
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <div className="profile-info">
          <div className="profile-photo-section">
            <img 
              src={getFileUrl(profile?.photo_url) || 'https://via.placeholder.com/120?text=Photo'} 
              alt="Photo de profil"
              className="profile-photo-display"
              onError={(e) => {
                (e.target as HTMLImageElement).src = 'https://via.placeholder.com/120?text=Photo'
              }}
            />
          </div>

          <div className="info-item">
            <label>Email:</label>
            <span>{userEmail}</span>
          </div>

          {!editing ? (
            <>
              <div className="info-row">
                <div className="info-item">
                  <label>Prénom:</label>
                  <span>{profile?.prenom || 'Non défini'}</span>
                </div>
                <div className="info-item">
                  <label>Nom:</label>
                  <span>{profile?.nom || 'Non défini'}</span>
                </div>
              </div>

              <div className="info-row">
                <div className="info-item">
                  <label>Téléphone:</label>
                  <span>{profile?.telephone || 'Non défini'}</span>
                </div>
              </div>

              <div className="info-row">
                <div className="info-item">
                  <label>Adresse:</label>
                  <span>{profile?.adresse || 'Non définie'}</span>
                </div>
              </div>

              <div className="info-row">
                <div className="info-item">
                  <label>Ville:</label>
                  <span>{profile?.ville || 'Non définie'}</span>
                </div>
                <div className="info-item">
                  <label>Code Postal:</label>
                  <span>{profile?.codePostal || 'Non défini'}</span>
                </div>
              </div>

              <button
                className="btn-primary"
                onClick={() => setEditing(true)}
              >
                Modifier le profil
              </button>
            </>
          ) : (
            <>
              <div className="photo-upload-section">
                <div className="photo-preview-container">
                  <img 
                    src={photoPreview || getFileUrl(profile?.photo_url) || 'https://via.placeholder.com/120?text=Photo'} 
                    alt="Aperçu de photo"
                    className="photo-preview"
                  />
                  <label className="photo-upload-label">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      style={{ display: 'none' }}
                    />
                    <span className="photo-upload-text">Cliquez pour changer</span>
                  </label>
                </div>
                {photoFile && <p className="photo-filename">Fichier sélectionné: {photoFile.name}</p>}
              </div>

              <div className="form-group">
                <label>Prénom</label>
                <input
                  type="text"
                  name="prenom"
                  value={formData.prenom || ''}
                  onChange={handleInputChange}
                  placeholder="Votre prénom"
                />
              </div>

              <div className="form-group">
                <label>Nom</label>
                <input
                  type="text"
                  name="nom"
                  value={formData.nom || ''}
                  onChange={handleInputChange}
                  placeholder="Votre nom"
                />
              </div>

              <div className="form-group">
                <label>Téléphone</label>
                <input
                  type="tel"
                  name="telephone"
                  value={formData.telephone || ''}
                  onChange={handleInputChange}
                  placeholder="Votre téléphone"
                />
              </div>

              <div className="form-group">
                <label>Adresse</label>
                <input
                  type="text"
                  name="adresse"
                  value={formData.adresse || ''}
                  onChange={handleInputChange}
                  placeholder="Votre adresse"
                />
              </div>

              <div className="form-group">
                <label>Ville</label>
                <input
                  type="text"
                  name="ville"
                  value={formData.ville || ''}
                  onChange={handleInputChange}
                  placeholder="Votre ville"
                />
              </div>

              <div className="form-group">
                <label>Code Postal</label>
                <input
                  type="text"
                  name="codePostal"
                  value={formData.codePostal || ''}
                  onChange={handleInputChange}
                  placeholder="Votre code postal"
                />
              </div>

              <div className="profile-actions">
                <button
                  className="btn-primary"
                  onClick={handleSaveProfile}
                  disabled={saving}
                >
                  {saving ? '⏳ Enregistrement...' : 'Enregistrer'}
                </button>
                <button
                  className="btn-secondary"
                  onClick={() => {
                    setEditing(false)
                    setFormData(profile || {})
                  }}
                  disabled={saving}
                >
                  Annuler
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
